import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FadeUpAnimation from '../../components/base/FadeUpAnimation';
import { useSEO, getSiteUrl } from '../../utils/seo';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  useSEO({
    title: 'お問い合わせ — そっと、話してみませんか | 2b LLC',
    description: '2b LLCへのお問い合わせページ。生成AI、政策、まちづくり、不動産、文章づくりなど、あなたの「こうなったらいいな」を一緒にかたちにしていきます。お気軽にご相談ください。',
    keywords: 'お問い合わせ,相談,コンタクト,2b LLC',
    canonical: getSiteUrl('/contact'),
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'お問い合わせ',
      description: '2b LLCへのお問い合わせフォーム',
      url: getSiteUrl('/contact'),
      mainEntity: {
        '@type': 'Organization',
        name: '合同会社2b',
        email: 'tobe26.2b@gmail.com',
        address: {
          '@type': 'PostalAddress',
          addressLocality: '世田谷区',
          addressRegion: '東京都',
          addressCountry: 'JP'
        }
      }
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch('https://readdy.ai/api/form/contact-2b', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
      }
    } catch (error) {
      console.error('送信エラー:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-[#F6F3EB]">
      <Header />
      
      <main className="pt-32">
        {/* Logo Section */}
        <section 
          className="py-12 relative"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/907cfde4a938040cba584afa8b86d64e.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-white/70"></div>
          <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
            <FadeUpAnimation>
              <img
                alt="2b LLC"
                className="w-[480px] h-auto mx-auto mb-12 object-contain"
                src="https://static.readdy.ai/image/721199a06948bc0907bde1e591c4b4e9/191bb64e22d504c48b73a6bcbfddf67c.png"
              />
            </FadeUpAnimation>
          </div>
        </section>

        {/* ① ヒーローセクション */}
        <section className="py-20 bg-[#F6F3EB]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <FadeUpAnimation delay={300}>
              <h1 className="text-4xl md:text-6xl font-semibold text-[#4A4A48] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Contact
              </h1>
            </FadeUpAnimation>
            
            <FadeUpAnimation delay={600}>
              <p className="text-xl md:text-2xl text-[#4A4A48] opacity-80" style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}>
                — そっと、話してみませんか。
              </p>
            </FadeUpAnimation>
          </div>
        </section>

        {/* メッセージセクション */}
        <section className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <FadeUpAnimation duration={1400}>
              <div className="text-center mb-20 max-w-4xl mx-auto">
                <div className="space-y-6 text-[#4A4A48] leading-[1.8]" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                  <p className="text-xs md:text-lg">まだ具体的なことが決まっていなくても大丈夫です。</p>
                  <p className="text-xs md:text-lg">「ちょっと相談したい」「聞いてみたい」から、気軽にどうぞ。</p>
                  <div className="space-y-2">
                    <p className="text-xs md:text-lg">生成AI、政策、まちづくり、不動産、文章づくり。</p>
                    <p className="text-xs md:text-lg">あなたが思う「こうなったらいいな」を一緒にかたちにしていきましょう。</p>
                  </div>
                </div>
              </div>
            </FadeUpAnimation>
          </div>
        </section>

        {/* ③ フォーム */}
        <section className="py-24 bg-[#F6F3EB] relative overflow-hidden">
          {/* 水面の波紋背景 */}
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=peaceful%20water%20surface%20with%20gentle%20ripples%20spreading%20in%20concentric%20circles%2C%20soft%20blue%20and%20white%20tones%2C%20calm%20lake%20water%20with%20subtle%20wave%20patterns%2C%20serene%20aquatic%20background%20with%20delicate%20ripple%20effects%2C%20minimalist%20water%20texture%20with%20natural%20light%20reflections%2C%20tranquil%20pond%20surface%20with%20expanding%20circular%20waves&width=1200&height=800&seq=water-ripples-bg&orientation=landscape"
              alt=""
              className="w-full h-full object-cover opacity-20"
            />
          </div>
          
          <div className="max-w-2xl mx-auto px-6 relative z-10">
            <FadeUpAnimation delay={200}>
              <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
                {isSubmitted ? (
                  <div className="text-center space-y-6">
                    <div className="w-16 h-16 bg-[#AFC5C2] rounded-full flex items-center justify-center mx-auto">
                      <i className="ri-check-line text-white text-2xl"></i>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-medium text-[#4A4A48]" style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}>
                      ありがとうございます
                    </h3>
                    <p className="text-lg md:text-xl text-[#4A4A48] leading-[1.75]" style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}>
                      内容を確認し、3営業日以内にご連絡いたします。
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-2b">
                    <div>
                      <label htmlFor="name" className="block text-sm md:text-base font-medium text-[#4A4A48] mb-2" style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}>
                        お名前 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-[#C8C6C3] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#AFC5C2] focus:border-transparent text-sm md:text-base"
                        style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm md:text-base font-medium text-[#4A4A48] mb-2" style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}>
                        メールアドレス <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-[#C8C6C3] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#AFC5C2] focus:border-transparent text-sm md:text-base"
                        style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm md:text-base font-medium text-[#4A4A48] mb-2" style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}>
                        件名
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="例：ご相談／業務依頼／協業提案 など"
                        className="w-full px-4 py-3 border border-[#C8C6C3] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#AFC5C2] focus:border-transparent text-sm md:text-base"
                        style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm md:text-base font-medium text-[#4A4A48] mb-2" style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}>
                        お問い合わせ内容
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={6}
                        value={formData.message}
                        onChange={handleChange}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-[#C8C6C3] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#AFC5C2] focus:border-transparent text-sm md:text-base resize-none"
                        style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                      />
                      <p className="text-xs text-[#A3A19E] mt-1" style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}>
                        {formData.message.length}/500文字
                      </p>
                    </div>

                    <div className="text-center pt-4">
                      <button
                        type="submit"
                        className="inline-flex items-center px-8 py-4 bg-[#4A4A48] text-white rounded-lg hover:bg-[#5B5A57] transition-colors duration-300 whitespace-nowrap cursor-pointer text-sm md:text-base"
                        style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                      >
                        <i className="ri-send-plane-line mr-3 text-lg"></i>
                        そっと送信
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </FadeUpAnimation>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
